import React, { useMemo, useState } from 'react';
import { ReactComponent as CheckIcon } from 'assets/icons/checkActive.svg';
import { useGetPage } from 'hooks/useGetPage';
import { pageIds } from 'utilities/constants';
import Loader from 'components/loaderComponent';
import { useGetMyBillingPlanQuery } from 'graphql/generated/hasura';
import { useAuth } from 'auth/context/AuthContext';
import CloseSubscriptionModal from './CloseSubscriptionModal';
import ButtonComponent from 'components/button/buttonComponent';
import {
  FINISH_PERIOD_TIME,
  SUBSCRIPTION_ACTIVE_STATUS,
  TRIAL_STATUS,
} from 'auth/context/AuthProvider';
import { sanitize } from 'utilities/sanitizer';
import Alert from 'components/alertComponent';
import moment from 'moment';
import { POLICY_DATE_FORMAT, SUBSCRIPTION_FORMAT } from 'utilities/dateHelper';

export const Benefit: React.FC<{ text: string }> = ({ text }) => {
  return (
    <p className="flex desktop:items-start items-center gap-2.5 text-neutral-800 font-medium">
      <CheckIcon className="desktop:w-6 desktop:h-6 desktop:max-w-[24px] desktop:min-w-[24px] w-5 h-5 min-w-[20px]" />
      {text}
    </p>
  );
};

const replace = (template: string, amount: number) => {
  const token = '{cost}';
  const replacements = [amount, amount / 3];
  let i = 0;
  return template.replace(new RegExp(token, 'g'), () => {
    return replacements[i++]?.toString() || '';
  });
};

const Benefits = () => {
  const { data: locale, loading } = useGetPage({
    locale: 'en',
    pageId: pageIds.MEMBERSHIP,
  });
  const [isOpen, setIsOpen] = useState(false);
  const { stripeSubscription } = useAuth();

  const { data: currentPlan } = useGetMyBillingPlanQuery();
  const plan = useMemo(() => currentPlan?.getMyBillingPlan, [currentPlan]);

  const hasActiveSubscription = useMemo(
    () =>
      moment().isSameOrBefore(
        moment(stripeSubscription?.[0]?.current_period_end).endOf('day'),
      ),
    [stripeSubscription],
  );

  const hasCanceledSub = !!stripeSubscription?.[0]?.cancelled_at;
  const dateEnd = moment(stripeSubscription?.[0]?.current_period_end)
    .endOf('day')
    .subtract(FINISH_PERIOD_TIME * -1, 'days')
    .format(POLICY_DATE_FORMAT);

  if (loading && !locale)
    return (
      <div className="p-30 bg-white rounded-10 flex justify-between gap-5 w-full">
        <Loader />
      </div>
    );

  if (hasCanceledSub) {
    return (
      <div className="p-30 bg-white rounded-10 flex flex-col justify-between gap-5 w-full">
        <h2 className="text-h1 text-neutral-800 font-medium">
          {locale?.title}
        </h2>
        <p className="font-medium text-neutral-800 text-base">
          {locale?.description}
        </p>

        <div className="flex flex-col gap-1.5">
          {(locale?.options ?? []).map((text: string) => (
            <Benefit key={text} text={text} />
          ))}
        </div>
        <Alert
          type="info-blue"
          iconClassName="hidden"
          customContent={
            <div className="flex flex-col gap-2.5 w-full">
              <h3 className="text-h3 font-semibold">
                Your subscription is now canceled
              </h3>
              <div className="flex w-full items-end">
                <p className="text-med-gray-3 font-medium text-base">
                  You can continue using all features until {dateEnd}. No
                  further charges will be made.
                </p>
              </div>
            </div>
          }
        />
      </div>
    );
  }

  return (
    <>
      <div className="p-30 bg-white rounded-10 flex flex-col justify-between gap-5 w-full">
        <h2 className="text-h1 text-neutral-800 font-medium">
          {locale?.title}
        </h2>
        <p className="font-medium text-neutral-800 text-base">
          {locale?.description}
        </p>

        <div className="flex flex-col gap-1.5">
          {(locale?.options ?? []).map((text: string) => (
            <Benefit key={text} text={text} />
          ))}
        </div>

        {hasActiveSubscription &&
          stripeSubscription?.[0]?.status === TRIAL_STATUS && (
            <Alert
              type="info-blue"
              iconClassName="hidden"
              customContent={
                <div className="flex flex-col gap-2.5 w-full">
                  <h3 className="text-h3 font-semibold">
                    Your free 30-day trial is active
                  </h3>
                  <div className="flex w-full items-end">
                    <p className="text-med-gray-3 font-medium text-base">
                      Enjoy full access to all features during your trial
                      period.
                    </p>
                    {SUBSCRIPTION_ACTIVE_STATUS.includes(
                      stripeSubscription?.[0]?.status ?? 0,
                    ) &&
                      !stripeSubscription?.[0]?.cancelled_at && (
                        <ButtonComponent
                          type="underline"
                          onClick={() => setIsOpen(true)}
                          testID="cancel-subscription"
                          className="ml-auto"
                        >
                          {locale?.cancelSubscriptions ?? 'Cancel subscription'}
                        </ButtonComponent>
                      )}
                  </div>
                </div>
              }
            />
          )}
        {hasActiveSubscription ? (
          stripeSubscription?.[0]?.status !== TRIAL_STATUS && (
            <Alert
              type="info-blue"
              iconClassName="hidden"
              customContent={
                <div className="flex flex-col gap-2.5 w-full">
                  <h3 className="text-h3 font-semibold">
                    Your subscription is now active for 3 months.{' '}
                  </h3>
                  <div className="flex w-full items-end">
                    <div>
                      <p
                        className="text-med-gray-3 font-medium text-base"
                        dangerouslySetInnerHTML={{
                          __html: sanitize(
                            replace(
                              'You have been charged <span class="text-neutral-800 font-semibold">${cost}</span> (${cost} per month).',
                              plan?.amount ?? 0,
                            ),
                          ),
                        }}
                      />
                      <p className="text-med-gray-3 font-medium text-base">
                        The next charge will occur in
                        <span className="text-neutral-800 font-semibold mx-1">
                          3 months
                        </span>
                        on{' '}
                        {moment(stripeSubscription?.[0]?.current_period_end)
                          .endOf('day')
                          .subtract(FINISH_PERIOD_TIME * -1, 'days')
                          .format(SUBSCRIPTION_FORMAT)}
                        .
                      </p>
                    </div>
                    {SUBSCRIPTION_ACTIVE_STATUS.includes(
                      stripeSubscription?.[0]?.status ?? 0,
                    ) &&
                      !stripeSubscription?.[0]?.cancelled_at && (
                        <ButtonComponent
                          type="underline"
                          onClick={() => setIsOpen(true)}
                          testID="cancel-subscription"
                          className="ml-auto"
                        >
                          {locale?.cancelSubscriptions ?? 'Cancel subscription'}
                        </ButtonComponent>
                      )}
                  </div>
                </div>
              }
            />
          )
        ) : (
          <Alert
            type="info"
            iconClassName="hidden"
            customContent={
              <div className="flex flex-col gap-2.5 w-full">
                <h3 className="text-h3 font-semibold">
                  Your subscription has expired. Renew now to restore full
                  access.
                </h3>
                <div>
                  <p
                    className="text-med-gray-3 font-medium text-base"
                    dangerouslySetInnerHTML={{
                      __html: sanitize(
                        replace(
                          'The amount <span class="text-neutral-800 font-semibold">${cost}</span> will be charged automatically.',
                          plan?.amount ?? 0,
                        ),
                      ),
                    }}
                  />
                  <p className="text-med-gray-3 font-medium text-base">
                    Your subscription will be extended for
                    <span className="text-neutral-800 font-semibold mx-1">
                      3 months
                    </span>
                    .
                  </p>
                </div>
              </div>
            }
          />
        )}

        <div className="flex flex-col gap-1.5 desktop:flex-row justify-between items-center" />
      </div>
      <CloseSubscriptionModal isOpen={isOpen} setIsOpen={setIsOpen} />
    </>
  );
};

export default Benefits;
